import { useTranslation } from 'react-i18next'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { HintTooltip } from '~/components/common/HintTooltip'
import { iconSize } from '~/constants/iconSize'
import type {
  MasterCheck,
  SupplyPoint,
} from '~/pages/CustomerInfo/models/types'

type Props = {
  point: Pick<SupplyPoint, 'supplyPointNumber' | 'masterCheck'>
}

/**
 * 契約一覧の供給地点特定番号の列。 番号未定の見込みは「(未定)」。
 * 供給地点マスタとの食い違いは行・列を増やさず、 番号の横に注意の印を出して
 * hover で違いを示す。 保存は止めない。
 */
export const SupplyPointNumberCell = ({ point }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { supplyPointNumber, masterCheck } = point
  const hint = masterCheckHint(masterCheck, t)
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {supplyPointNumber === undefined ? (
        <span style={{ color: LightModeSemanticColors.Text.lowEmphasis.value }}>
          {t('page:customerInfo.common.undecided')}
        </span>
      ) : (
        supplyPointNumber
      )}
      {hint !== undefined && (
        <HintTooltip content={hint}>
          <span
            aria-label={hint}
            role="img"
            style={{
              marginLeft: '6px',
              verticalAlign: 'middle',
              color: LightModeSemanticColors.Text.warningMid.value,
            }}
          >
            <FaTriangleExclamationSolid boxSize={iconSize.gridInline} />
          </span>
        </HintTooltip>
      )}
    </span>
  )
}

const masterCheckHint = (
  check: MasterCheck,
  t: (key: string, options?: Record<string, string>) => string,
): string | undefined => {
  if (check.kind === 'match') return undefined
  if (check.kind === 'notInMaster') {
    return t('page:customerInfo.masterCheck.notInMaster')
  }
  const items = check.mismatches
    .map((m) => t(`page:customerInfo.masterCheck.item.${m}`))
    .join(t('page:customerInfo.masterCheck.itemSeparator'))
  return t('page:customerInfo.masterCheck.mismatch', { items })
}
